/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

(function () {
  function initStickyAddToCart() {
    var buyButtonsRow = document.querySelector('.section-main-product .buy-buttons-row');
    if (!buyButtonsRow) return;

    var mainButton = buyButtonsRow.querySelector('.add-to-cart');
    if (!mainButton) return;

    var titleEl = document.querySelector('.section-main-product .product-title');
    var priceEl = document.querySelector('.section-main-product .product-price');

    var bar = document.createElement('div');
    bar.className = 'sticky-atc-bar';
    bar.innerHTML =
      '<div class="sticky-atc-bar__inner">' +
        '<div class="sticky-atc-bar__info">' +
          '<span class="sticky-atc-bar__title"></span>' +
          '<span class="sticky-atc-bar__price"></span>' +
        '</div>' +
        '<button type="button" class="btn sticky-atc-bar__btn"></button>' +
      '</div>';
    document.body.appendChild(bar);

    var barTitle = bar.querySelector('.sticky-atc-bar__title');
    var barPrice = bar.querySelector('.sticky-atc-bar__price');
    var barBtn = bar.querySelector('.sticky-atc-bar__btn');

    function syncBar() {
      if (titleEl) barTitle.textContent = titleEl.textContent.trim();
      if (priceEl) barPrice.textContent = priceEl.textContent.trim();
      barBtn.textContent = mainButton.textContent.trim();
      barBtn.disabled = mainButton.disabled;
    }

    syncBar();

    barBtn.addEventListener('click', function () {
      mainButton.click();
    });

    document.addEventListener('on:variant:change', syncBar);

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.classList.toggle('sticky-atc-bar--visible', scrolledPast);
        });
      }, { threshold: 0 });
      observer.observe(buyButtonsRow);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyAddToCart);
  } else {
    initStickyAddToCart();
  }
})();

(function () {
  function initEditorialTabs() {
    if (!document.body.classList.contains('template-suffix-editorial')) return;

    var panels = document.querySelectorAll('.section-main-product .product-info-accordion');
    if (panels.length < 2) return;

    var tabBar = document.createElement('div');
    tabBar.className = 'editorial-tabs';
    tabBar.setAttribute('role', 'tablist');

    var buttons = [];

    function setActive(index) {
      panels.forEach(function (otherPanel, otherIndex) {
        var otherDetails = otherPanel.querySelector('details');
        var isActive = otherIndex === index;
        if (otherDetails) otherDetails.open = isActive;
        otherPanel.hidden = !isActive;
        if (buttons[otherIndex]) {
          buttons[otherIndex].classList.toggle('editorial-tabs__btn--active', isActive);
        }
      });
    }

    panels.forEach(function (panel, index) {
      var details = panel.querySelector('details');
      var summary = panel.querySelector('summary');
      if (!details || !summary) return;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'editorial-tabs__btn';
      btn.setAttribute('role', 'tab');
      btn.textContent = summary.textContent.trim();

      btn.addEventListener('click', function () {
        setActive(index);
      });

      buttons.push(btn);
      tabBar.appendChild(btn);
    });

    setActive(0);
    panels[0].parentNode.insertBefore(tabBar, panels[0]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initEditorialTabs);
  } else {
    initEditorialTabs();
  }
})();

(function () {
  var STORAGE_KEY = 'zanatany_recently_viewed';
  var MAX_ITEMS = 12;

  function readViewed() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function writeViewed(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      /* localStorage unavailable — skip silently */
    }
  }

  function trackCurrentProduct() {
    var dataEl = document.getElementById('zanatany-current-product');
    if (!dataEl) return;

    var current;
    try {
      current = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!current || !current.handle) return;

    var list = readViewed().filter(function (item) {
      return item.handle !== current.handle;
    });
    list.unshift(current);
    if (list.length > MAX_ITEMS) list.length = MAX_ITEMS;
    writeViewed(list);
  }

  function buildCard(item) {
    var wrap = document.createElement('product-block');
    wrap.className = 'product-block';

    var moreLabel = '';
    if (item.colorCount > 1) {
      moreLabel =
        '<div class="product-block-options product-block-options--swatch">' +
          '<div class="product-block-options__inner">' +
            '<span class="product-block-options__more-label">+ ' + (item.colorCount - 1) + '</span>' +
          '</div>' +
        '</div>';
    }

    wrap.innerHTML =
      '<div class="block-inner"><div class="block-inner-inner">' +
        '<div class="image-cont">' +
          '<a class="product-link" href="' + item.url + '">' +
            '<div class="image-label-wrap"><div>' +
              '<div class="product-block__image product-block__image--primary product-block__image--active">' +
                '<img src="' + item.image + '" alt="' + item.title.replace(/"/g, '&quot;') + '" loading="lazy">' +
              '</div>' +
            '</div></div>' +
          '</a>' +
        '</div>' +
        '<div class="product-block__detail align-ltr-center"><div class="inner"><div class="innerer">' +
          '<a class="product-link" href="' + item.url + '">' +
            '<div class="product-block__title">' + item.title + '</div>' +
            '<div class="product-price product-price--block">' + item.price + '</div>' +
          '</a>' +
          moreLabel +
        '</div></div></div>' +
      '</div></div>';

    return wrap;
  }

  function initRecentlyViewed(root) {
    root = root || document;
    var tabs = root.querySelectorAll('[data-rv-tab]');
    var panels = root.querySelectorAll('[data-rv-panel]');
    var grid = root.querySelector('[data-rv-grid]');
    var emptyMsg = root.querySelector('[data-rv-empty]');

    if (!tabs.length || !grid) return;

    var currentEl = document.getElementById('zanatany-current-product');
    var currentHandle = null;
    if (currentEl) {
      try {
        currentHandle = JSON.parse(currentEl.textContent).handle;
      } catch (e) {}
    }

    var viewed = readViewed().filter(function (item) {
      return item.handle !== currentHandle;
    });

    grid.innerHTML = '';
    if (viewed.length === 0) {
      if (emptyMsg) emptyMsg.hidden = false;
    } else {
      if (emptyMsg) emptyMsg.hidden = true;
      viewed.forEach(function (item) {
        grid.appendChild(buildCard(item));
      });
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var target = tab.getAttribute('data-rv-tab');
        tabs.forEach(function (t) {
          t.classList.toggle('recently-viewed-tab--active', t === tab);
        });
        panels.forEach(function (panel) {
          panel.hidden = panel.getAttribute('data-rv-panel') !== target;
        });
      });
    });
  }

  window.ZanatanyInitRecentlyViewed = initRecentlyViewed;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trackCurrentProduct);
  } else {
    trackCurrentProduct();
  }
})();

(function () {
  function initVariantPriceLabel() {
    if (!document.body.classList.contains('template-suffix-editorial')) return;

    var priceEl = document.querySelector('.section-main-product .price-container .product-price');
    if (!priceEl) return;

    var label = document.createElement('span');
    label.className = 'editorial-variant-label';
    priceEl.insertAdjacentElement('beforebegin', label);

    function setLabel(text) {
      label.textContent = text ? text + ' — ' : '';
    }

    var colorText = document.querySelector('.section-main-product .js-color-text');
    if (colorText) {
      setLabel(colorText.textContent.trim());
    } else {
      var dropdownBtn = document.querySelector('.section-main-product .option-selector[data-selector-type="dropdown"] .custom-select__btn[data-swatch] .text-start');
      if (dropdownBtn) setLabel(dropdownBtn.textContent.trim());
    }

    document.addEventListener('on:variant:change', function (evt) {
      var selectedOptions = evt.detail && evt.detail.selectedOptions;
      setLabel(selectedOptions && selectedOptions[0] ? selectedOptions[0] : '');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVariantPriceLabel);
  } else {
    initVariantPriceLabel();
  }
})();
